import CharacterShopSection from '@/components/shop/CharacterShopSection';
import PropShopSection from '@/components/shop/PropShopSection';
import { CurrencyDisplay, Modal, PixelIcon } from '@/components/ui';
import { useShop } from '@/contexts/ShopContext';
import { useSound } from '@/contexts/SoundContext';
import { PROPS_CATALOG } from '@/types/props';
import { motion } from 'motion/react';
import React, { useState } from 'react';
import type { CSSProperties } from 'react';
import { FONT_SIZE, SPACING, COLOR, resourceChipStyle } from '@/config/designTokens';

interface ShopModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ShopItem {
  id: string;
  name: string;
  description: string;
  price: number;
  currency: 'coins' | 'gems';
  icon: string;
  category: 'powerups' | 'lives' | 'themes' | 'upgrades';
  available: number;
  owned?: number;
}

const ShopModal: React.FC<ShopModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<string>('powerups');
  const { shopState, purchasePowerup, shopPowerups, shopManager } = useShop();
  const { playButtonClick } = useSound();

  // Get streak info
  const streakStatus = shopManager?.getDailyRewardStatus() || { streak: 0, canClaim: false };

  // Use real shop data
  const playerCurrency = { coins: shopState.coins, gems: shopState.gems, lives: shopState.lives };

  // Convert shop powerups to shop items format
  const shopItems: ShopItem[] = Object.entries(shopPowerups).map(([id, powerup]) => ({
    id,
    name: powerup.name,
    description: powerup.description,
    price: powerup.cost,
    currency: powerup.currency,
    icon: powerup.icon,
    category: powerup.category === 'lives' ? 'lives' : 'powerups',
    available: powerup.maxQuantity - (shopState.powerups[powerup.id] || 0),
    owned: shopState.powerups[powerup.id] || 0,
  }));

  const tabs = [
    {
      id: 'powerups',
      label: 'Power-ups',
      icon: 'lightning',
      count: shopItems.filter(item => item.category === 'powerups').length,
    },
    {
      id: 'characters',
      label: 'Characters',
      icon: 'user',
      count: shopState.ownedCharacters.length,
    },
    {
      id: 'props',
      label: 'Props',
      icon: 'settings',
      count: PROPS_CATALOG.length,
    },
    {
      id: 'lives',
      label: 'Lives',
      icon: 'life',
      count: shopItems.filter(item => item.category === 'lives').length,
    },
    { id: 'themes', label: 'Themes', icon: 'palette', count: 4 },
  ];

  const filteredItems = shopItems.filter(
    item => activeTab === 'all' || item.category === activeTab
  );

  const handlePurchase = (item: ShopItem) => {
    const cost = item.price;
    const playerBalance = playerCurrency[item.currency];

    if (playerBalance >= cost && item.available > 0) {
      const success = purchasePowerup(item.id as any);
      if (success) {
        playButtonClick();
        console.log(`Successfully purchased ${item.name}!`);
      } else {
        playButtonClick();
        console.log('Purchase failed');
      }
    } else {
      playButtonClick();
      console.log('Insufficient funds or item unavailable');
    }
  };

  const canAfford = (item: ShopItem) => {
    return playerCurrency[item.currency] >= item.price;
  };

  const renderCurrencyDisplay = () => (
    <div className="menu-toolbar">
      <CurrencyDisplay
        coins={playerCurrency.coins}
        gems={playerCurrency.gems}
        lives={playerCurrency.lives}
        streak={streakStatus.streak}
        canClaimStreak={streakStatus.canClaim}
        variant="header"
        showStreak={true}
        animated={true}
      />
    </div>
  );

  const renderShopItems = () => (
    <div className="shop-power-grid">
      {filteredItems.map((item, index) => (
        <motion.div
          key={item.id}
          className={`shop-power-card ${canAfford(item) ? '' : 'is-unaffordable'}`}
          style={
            {
              '--card-border': canAfford(item) ? '#53d37f' : '#5f6b78',
            } as CSSProperties
          }
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05, duration: 0.3 }}
          whileHover={{ scale: 1.02 }}
        >
          {/* Item Header */}
          <div className="shop-power-card__header">
            <div className="shop-power-card__icon">
              <PixelIcon name={item.icon} size={48} />
            </div>
            <h4 className="shop-power-card__title">{item.name}</h4>
            <p className="shop-power-card__description">{item.description}</p>
          </div>

          {/* Item Stats */}
          <div className="shop-power-card__stats">
            {item.owned !== undefined && (
              <div className="shop-power-card__stats-row">
                <span>Owned:</span>
                <span className="shop-power-card__value">{item.owned}</span>
              </div>
            )}
            <div className="shop-power-card__stats-row">
              <span>Available:</span>
              <span className="shop-power-card__value">
                {item.available === 999 ? '∞' : item.available}
              </span>
            </div>
          </div>

          {/* Purchase Button */}
          <motion.button
            className="shop-power-card__buy"
            onClick={() => handlePurchase(item)}
            disabled={!canAfford(item) || item.available === 0}
            whileHover={canAfford(item) ? { scale: 1.02 } : {}}
            whileTap={canAfford(item) ? { scale: 0.98 } : {}}
          >
            <PixelIcon
              name={item.currency === 'coins' ? 'coin' : 'gem'}
              size={16}
              color={item.currency === 'coins' ? '#f39c12' : '#9b59b6'}
            />
            <span>{item.available === 0 ? 'Sold Out' : `${item.price}`}</span>
          </motion.button>
        </motion.div>
      ))}
    </div>
  );

  const renderComingSoon = () => (
    <motion.div
      className="shop-empty-state"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="shop-empty-state__icon">
        <PixelIcon name="construction" size={72} color="#f39c12" />
      </div>
      <h3>Coming Soon!</h3>
      <p>This section is under construction.</p>
    </motion.div>
  );

  const mobileBarActions = (
    <div
      style={{
        display: 'flex',
        gap: SPACING.lg,
        width: '100%',
        justifyContent: 'space-around',
      }}
    >
      <div style={{ ...resourceChipStyle(), gap: SPACING.sm }}>
        <PixelIcon name="coin" size={24} color={COLOR.coin} />
        <span style={{ fontSize: FONT_SIZE.statValue, fontWeight: 'bold', color: COLOR.coin }}>
          {playerCurrency.coins}
        </span>
      </div>
      <div style={{ ...resourceChipStyle(), gap: SPACING.sm }}>
        <PixelIcon name="gem" size={24} color={COLOR.gem} />
        <span style={{ fontSize: FONT_SIZE.statValue, fontWeight: 'bold', color: COLOR.gem }}>
          {playerCurrency.gems}
        </span>
      </div>
      <div style={{ ...resourceChipStyle(), gap: SPACING.sm }}>
        <PixelIcon name="life" size={24} color={COLOR.life} />
        <span style={{ fontSize: FONT_SIZE.statValue, fontWeight: 'bold', color: COLOR.life }}>
          {playerCurrency.lives}
        </span>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Power Shop"
      subtitle="Tune your loadout. Prepare for the next world."
      variant="warning"
      size="large"
      className="shop-modal menu-facelift-modal"
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      headerContent={renderCurrencyDisplay()}
      showMobileBar={false}
      layoutId="nav-button-shop"
      sourceButtonId="shop"
    >
      {activeTab === 'props' ? (
        <PropShopSection filter="all" />
      ) : activeTab === 'characters' ? (
        <CharacterShopSection filter={activeTab} />
      ) : activeTab === 'themes' ? (
        renderComingSoon()
      ) : activeTab === 'powerups' || activeTab === 'lives' ? (
        <>
          {filteredItems.length > 0 ? (
            renderShopItems()
          ) : (
            <motion.div
              className="shop-empty-state"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="shop-empty-state__icon">
                <PixelIcon name="error" size={72} color="#f56565" />
              </div>
              <p>No items available in this category.</p>
            </motion.div>
          )}
        </>
      ) : (
        renderComingSoon()
      )}
    </Modal>
  );
};

export default ShopModal;
